<template>
  <div class="report-completion-ring pa-6">
    <div class="report-completion-ring__chart">
      <doughnut-chart :data="chartData" :options="chartOptions" />
    </div>
    <div class="body2 text-center mt-2" data-testid="report-completion-ring-caption">
      {{ caption }}
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import type { ScriptableContext } from "chart.js";
import DoughnutChart from "@/components/DoughnutChart.vue";
import { DoughnutChartColors } from "@/constants/charts";

/**
 * Chart.js scriptable colour for the ring: the completed arc gets a violet-to-blue gradient
 * running bottom-left to top-right, the remainder stays flat grey.
 * Chart.js calls this before layout too, when `chartArea` is still undefined, hence the flat
 * fallback — returning `null` there is not a valid `Color` and breaks the dataset's type.
 * @param context - The scriptable context, used for the canvas, plot bounds and slice index.
 * @returns The gradient for the completed slice, otherwise a flat theme colour.
 */
const sliceColor = (context: ScriptableContext<"doughnut">): CanvasGradient | string => {
  if (context.dataIndex !== 0) {
    return DoughnutChartColors.GRAY;
  }
  const { ctx, chartArea } = context.chart;
  if (!chartArea) {
    return DoughnutChartColors.BLUE;
  }
  const gradient = ctx.createLinearGradient(
    chartArea.left,
    chartArea.bottom,
    chartArea.right,
    chartArea.top
  );
  gradient.addColorStop(0, DoughnutChartColors.PINK);
  gradient.addColorStop(1, DoughnutChartColors.BLUE);
  return gradient;
};

/**
 * How wide the text inside the ring may wrap.
 *
 * Bounds the SUBTITLE's word wrapping. It has to stay within the square that the doughnut's hole
 * inscribes: at the 250px box below, Chart.js pads 10px a side and cuts out 82% of the radius, so
 * the hole is 189px across and the square inside it is 133px; 130 leaves a hair of margin. The
 * plugin's own default of 150px is wider than that square, which let a line such as "trainings
 * completion" (143px) spill over the stroke.
 *
 * The centre TITLE no longer depends on this number — the plugin bounds it by the ring's measured
 * geometry, so "100%" cannot overflow at any box size. On the previous 190px box that square was
 * only 99px while "100%" at `500 48px Quicksand` measures 114px, so the largest possible value
 * was the one guaranteed to overflow.
 */
const INSIDE_TEXT_MAX_WIDTH = 130;

export default defineComponent({
  components: { DoughnutChart },
  props: {
    completed: {
      type: Number,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    caption: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    /**
     * The completed share clamped to 0..1 — the single value behind BOTH the arc and the label.
     *
     * They used to be guarded separately and disagreed in both directions: `{ completed: 5,
     * total: 0 }` drew a fully closed ring captioned "0%", because the label's guard was
     * `total > 0` while the arc's was `completed + remaining > 0`; and 594 of 595 drew a visible
     * grey gap captioned "100%", because the label rounded up past what the arc showed.
     */
    const completedRatio = computed(() =>
      props.total > 0 ? Math.min(1, Math.max(0, props.completed / props.total)) : 0
    );

    // Rounded once, and the ARC is then drawn from this same number rather than from the raw
    // counts — of the two ways to make them agree, this is the one that changes no figure anyone
    // has already seen: 446 of 595 still reads "75%", and 594 of 595 now closes the ring to match
    // its own "100%" instead of leaving a visible gap beside it.
    const percentage = computed(() => Math.round(completedRatio.value * 100));

    const chartData = computed(() => {
      const completed = percentage.value / 100;
      return {
        labels: [props.label, ""],
        datasets: [
          {
            // The clamped ratio, not the raw counts, so the arc is drawn from the same number the
            // label prints. A zero-sum dataset makes Chart.js draw no arc at all, so a workspace
            // with no trainings lost the ring outright — the caption read "0%" beside empty
            // space; `[0, 1]` paints the full grey track, which is what 0% looks like.
            data: completed > 0 ? [completed, 1 - completed] : [0, 1],
            backgroundColor: sliceColor,
            cutout: "82%",
          },
        ],
      };
    });

    const chartOptions = computed(() => ({
      // Chart.js keeps the canvas at its aspect ratio by default, so on a narrower track the
      // canvas stays wider than its box and the report card (overflow: hidden) clips the ring.
      // Letting the chart fill the box instead keeps the arc circular — a doughnut sizes itself
      // from the smaller dimension — and shrinks with the column.
      responsive: true,
      maintainAspectRatio: false,
      // Static figure, so nothing here reacts to a pointer. An empty `events` list is the whole
      // fix: Chart.js registers no listeners at all, which takes out the tooltip, the arc's
      // hover pop and DoughnutChart's `onHover` pointer cursor in one go. The other two are
      // belt and braces — they also state the intent where a reader looks for it.
      events: [],
      hoverOffset: 0,
      plugins: { tooltip: { enabled: false } },
      insideTextData: {
        title: `${percentage.value}%`,
        subtitle: props.label,
        maxWidth: INSIDE_TEXT_MAX_WIDTH,
      },
    }));

    return { chartData, chartOptions };
  },
});
</script>

<style scoped lang="scss">
.report-completion-ring {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;

  // Matched to ExecutiveSummaryBotAttacksCard's donut so the report's two ring figures read as
  // one family. Both dimensions stay fixed rather than `height: auto`: the PDF export swaps each
  // canvas for an `<img>`, and a wrapper of known height is what keeps the exported layout — and
  // therefore the page breaks — from moving with the snapshot's aspect ratio.
  &__chart {
    width: 250px;
    height: 250px;
    max-width: 100%;
    margin: 0 auto;

    :deep(canvas) {
      max-width: 100%;
    }
  }
}
</style>
